import { useState, useEffect, type FormEvent } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import * as api from "@/lib/api";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";

export default function SponsorEditPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [name, setName] = useState("");
  const [fullName, setFullName] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [industry, setIndustry] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    api
      .getSponsor(id)
      .then((res) => {
        setName(res.sponsor.name);
        setFullName(res.sponsor.full_name ?? "");
        setWebsiteUrl(res.sponsor.website_url ?? "");
        setIndustry(res.sponsor.industry ?? "");
        setContactName(res.sponsor.contact_name);
        setContactEmail(res.sponsor.contact_email);
        setContactPhone(res.sponsor.contact_phone ?? "");
      })
      .catch((e) =>
        setError(e instanceof api.ApiError ? e.message : "Failed to load sponsor"),
      )
      .finally(() => setLoading(false));
  }, [id]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!id) return;
    setError(null);
    setSubmitting(true);

    try {
      await api.updateSponsor(id, {
        name,
        full_name: fullName || null,
        website_url: websiteUrl || null,
        industry: industry || null,
        contact_name: contactName,
        contact_email: contactEmail,
        contact_phone: contactPhone || null,
      });
      navigate(`/sponsors/${id}`);
    } catch (err) {
      setError(err instanceof api.ApiError ? err.message : "Failed to update sponsor");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return <p className="text-muted-foreground" data-page="sponsor-edit" data-state="loading">Loading...</p>;
  }

  return (
    <div className="flex flex-col gap-6" data-page="sponsor-edit" data-state={submitting ? "submitting" : error ? "error" : "ready"}>
      <div>
        <h1 className="text-2xl font-semibold">Edit Sponsor</h1>
        <p className="text-muted-foreground">Update sponsor details</p>
      </div>

      <Card className="max-w-lg">
        <CardHeader>
          <CardTitle className="text-base">Sponsor Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4" data-region="form" data-form="sponsor-edit">
            {error && <p className="text-sm text-destructive" data-error>{error}</p>}

            <div className="flex flex-col gap-2">
              <Label htmlFor="name">Sponsor Name</Label>
              <Input
                id="name"
                data-field="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="fullName">Full Company Name</Label>
              <Input
                id="fullName"
                data-field="full-name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="websiteUrl">Website URL</Label>
              <Input
                id="websiteUrl"
                data-field="website-url"
                type="url"
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="industry">Industry</Label>
              <Input
                id="industry"
                data-field="industry"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="contactName">Contact Name</Label>
              <Input
                id="contactName"
                data-field="contact-name"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                required
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="contactEmail">Contact Email</Label>
              <Input
                id="contactEmail"
                data-field="contact-email"
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                required
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="contactPhone">Contact Phone</Label>
              <Input
                id="contactPhone"
                data-field="contact-phone"
                placeholder="e.g. +27 11 123 4567"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
              />
            </div>

            <div className="flex flex-col gap-2 sm:flex-row">
              <Button type="submit" data-action="submit" disabled={submitting}>
                {submitting ? "Saving..." : "Save Changes"}
              </Button>
              <Link to={`/sponsors/${id}`} className={buttonVariants({ variant: "outline" })} data-action="cancel">
                Cancel
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
